import { IMenuTreeItem, ITrain } from "@framework/provider-asu";
import { DEFAULT_TCI_LEVELS, ITCILevels } from "@framework/provider-settings";
import { EquipmentStateDisplayMode } from "@framework/provider-user";

import { getDefaultAlarmColor, isSupportedColorValue, parseColor } from "../../../../utils";
import { getMaxAlarmsStatusPointsOrVisualControl } from "../../../../utils/get-max-alarm-status";

const DEFAULT_MODE_BORDER_COLOR = "#363846";

const normalizeModeColorValue = (color: unknown): string | null => {
  if (color == null) {
    return null;
  }

  if (typeof color === "number" && Number.isFinite(color)) {
    return String(Math.trunc(color));
  }

  if (typeof color === "string") {
    const trimmed = color.trim();
    return trimmed || null;
  }

  return null;
};

export interface IResolveEquipmentIconColorOptions {
  equipmentStateDisplayMode: EquipmentStateDisplayMode;
  isVisualControlOpen: boolean;
  tciLevels?: ITCILevels;
  /** Из профиля: учитывает пользовательские цвета сигнализации (как на графиках/схеме). */
  resolveAlarmColor?: (status: number) => string;
}

export const getTciColor = (
  tciValue: number | null | undefined,
  tciLevels: ITCILevels,
  resolveAlarmColor: (status: number) => string,
): string => {
  if (typeof tciValue !== "number" || Number.isNaN(tciValue)) {
    return resolveAlarmColor(0);
  }

  if (tciValue > tciLevels.warning) {
    return resolveAlarmColor(30);
  }

  if (tciValue > tciLevels.critical) {
    return resolveAlarmColor(60);
  }

  return resolveAlarmColor(90);
};

export const getModeColor = (
  modeChangedAt: string | null | undefined,
  modeColor: unknown,
  resolveAlarmColor: (status: number) => string,
): string | null => {
  if (!modeChangedAt || Number.isNaN(new Date(modeChangedAt).getTime())) {
    return null;
  }

  const normalizedColor = normalizeModeColorValue(modeColor);
  if (!normalizedColor || !isSupportedColorValue(normalizedColor)) {
    return resolveAlarmColor(0);
  }

  return parseColor(normalizedColor);
};

const resolveModeColorValue = (modeColor: unknown): string | null => {
  const normalizedColor = normalizeModeColorValue(modeColor);
  if (!normalizedColor) {
    return null;
  }

  if (isSupportedColorValue(normalizedColor)) {
    return parseColor(normalizedColor);
  }

  return null;
};

export const resolveTrainModeBorderColor = (
  modeChangedAt: string | null | undefined,
  modeColor: unknown,
  resolveAlarmColor: (status: number) => string,
  fallbackColor: string = DEFAULT_MODE_BORDER_COLOR,
): string => {
  const fromMode = getModeColor(modeChangedAt, modeColor, resolveAlarmColor);
  if (fromMode) {
    return fromMode;
  }

  return resolveModeColorValue(modeColor) ?? fallbackColor;
};

export const resolveTrainIconColor = (
  train: ITrain,
  {
    equipmentStateDisplayMode,
    isVisualControlOpen,
    tciLevels = DEFAULT_TCI_LEVELS,
    resolveAlarmColor = getDefaultAlarmColor,
  }: IResolveEquipmentIconColorOptions,
): string => {
  const gostColor = resolveAlarmColor(getMaxAlarmsStatusPointsOrVisualControl(train.Points, isVisualControlOpen));

  switch (equipmentStateDisplayMode) {
    case EquipmentStateDisplayMode.TCI:
      return getTciColor(train.TciValue, tciLevels, resolveAlarmColor);
    case EquipmentStateDisplayMode.MODE:
      return getModeColor(train.ModeChangedAt, train.ModeColor, resolveAlarmColor) ?? gostColor;
    case EquipmentStateDisplayMode.GOST:
    default:
      return gostColor;
  }
};

export const resolveStationIconColor = (
  station: IMenuTreeItem,
  {
    equipmentStateDisplayMode,
    tciLevels = DEFAULT_TCI_LEVELS,
    resolveAlarmColor = getDefaultAlarmColor,
  }: IResolveEquipmentIconColorOptions,
): string => {
  const gostColor = resolveAlarmColor(station.MaxAlarmStatus || 0);

  switch (equipmentStateDisplayMode) {
    case EquipmentStateDisplayMode.TCI:
      return getTciColor(station.TciValue, tciLevels, resolveAlarmColor);
    case EquipmentStateDisplayMode.MODE:
      return getModeColor(station.ModeChangedAt, station.ModeColor, resolveAlarmColor) ?? gostColor;
    case EquipmentStateDisplayMode.GOST:
    default:
      return gostColor;
  }
};
